import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Sort} from '@angular/material/sort';
import {BaseService, User, UUID} from '@nh/angular-tses';
import {environment} from 'environments/environment';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {UserGroup, UserGroupType} from '../../admin/admin-user-groups/user-group';

export interface Discipline extends UserGroup {
  readonly type: UserGroupType.DISCIPLINE;
}

export function groupIsDiscipline(group: UserGroup): group is Discipline {
  return group.type === UserGroupType.DISCIPLINE;
}

export interface DisciplineResults {
  disciplines: Discipline[];
  total: number;
}

export interface UserDisciplineMapping {
  id: string;
  userId: UUID;
  disciplineId: UUID;
  createdAt: number;
  createdBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class DisciplineService extends BaseService {
  private static readonly DISCIPLINES_ENDPOINT: string = `${environment.tsesConfig.apiHost}/disciplines`;

  constructor(snackbar: MatSnackBar, private http: HttpClient) {
    super(snackbar);
  }

  public getDiscipline(disciplineId: UUID | string): Observable<Discipline> {
    if (disciplineId instanceof UUID) {
      disciplineId = disciplineId.value;
    }
    const idValue: string = disciplineId;
    const url: string = `${DisciplineService.DISCIPLINES_ENDPOINT}/${idValue}`;
    return this.http.get<Discipline>(url, {}).pipe(
      map(json => this.deserialise(json)),
      catchError((err: any) => {
        this._handleError(err, `Failed to get discipline with id: ${idValue}`);
        return of(null);
      })
    );
  }

  public getAllDisciplines(): Observable<Discipline[]> {
    const url: string = `${DisciplineService.DISCIPLINES_ENDPOINT}/`;
    return this.http.get<Discipline[]>(url, {}).pipe(
      map(json => this.deserialiseArray(json)),
      catchError((err: any) => {
        this._handleError(err, 'Failed to get disciplines from database.');
        return of([]);
      })
    );
  }

  public getAUsersDisciplines(userId: UUID | string): Observable<Discipline[]> {
    if (userId instanceof UUID) {
      userId = userId.value;
    }
    const url = `${DisciplineService.DISCIPLINES_ENDPOINT}/user/${userId}`;
    return this.http.get<Discipline[]>(url, {}).pipe(
      map(json => this.deserialiseArray(json)),
      catchError((err: any) => {
        this._handleError(err, `Failed to get this user's disciplines.`);
        return of([]);
      })
    );
  }

  public getUsersByDisciplineId(disciplineId: UUID | string): Observable<User[]> {
    if (disciplineId instanceof UUID) {
      disciplineId = disciplineId.value;
    }
    const url = `${DisciplineService.DISCIPLINES_ENDPOINT}/users-in-discipline/${disciplineId}`;

    return this.http.get(url).pipe(map(json => this.mapToUserArray(json)));
  }

  public getUsersByNotDisciplineId(disciplineId: UUID | string): Observable<User[]> {
    if (disciplineId instanceof UUID) {
      disciplineId = disciplineId.value;
    }
    const url = `${DisciplineService.DISCIPLINES_ENDPOINT}/users-not-in-discipline/${disciplineId}`;

    return this.http.get(url).pipe(map(json => this.mapToUserArray(json)));
  }

  public addUserToDiscipline(
    disciplineId: UUID | string,
    userId: UUID | string,
    successMessage?: string
  ): Observable<UserDisciplineMapping> {
    if (disciplineId instanceof UUID) {
      disciplineId = disciplineId.value;
    }
    if (userId instanceof UUID) {
      userId = userId.value;
    }
    const url = `${DisciplineService.DISCIPLINES_ENDPOINT}/${disciplineId}/${userId}`;
    return this.http
      .post<UserDisciplineMapping>(url, {})
      .pipe(
        catchError(err =>
          throwError(this._handleError(err, `Failed to add user ${userId} to discipline ${disciplineId}`))
        )
      );
  }

  public removeUserFromDiscipline(disciplineId: UUID | string, userId: UUID | string): Observable<any> {
    if (disciplineId instanceof UUID) {
      disciplineId = disciplineId.value;
    }
    if (userId instanceof UUID) {
      userId = userId.value;
    }
    const url = `${DisciplineService.DISCIPLINES_ENDPOINT}/discipline/${disciplineId}/user/${userId}`;
    return this.http
      .delete(url, {})
      .pipe(
        catchError(err =>
          throwError(this._handleError(err, `Failed to remove user ${userId} from discipline ${disciplineId}`))
        )
      );
  }

  public searchDisciplines(
    search_query: string,
    page: number,
    pageSize: number,
    sort: Sort
  ): Observable<DisciplineResults> {
    const url = `${environment.tsesConfig.apiHost}/disciplines-search/search/search`;
    const params: {[param: string]: string} = {
      search_query: search_query,
      page: !page ? '0' : String(page),
      size: !pageSize ? '10' : String(pageSize),
      sort: !sort ? null : sort.active + ',' + sort.direction
    };

    return this.http.get(url, {params}).pipe(
      map(json => this.mapJsonToDisciplineResults(json)),
      catchError((err: any) => {
        this._handleError(err, 'Failed to perform discipline search.');
        return of(null);
      })
    );
  }

  private mapJsonToDisciplineResults(organisationsJson): DisciplineResults {
    const disciplines: Discipline[] = this.deserialiseArray(organisationsJson._embedded.disciplines);
    const total = organisationsJson.page.totalElements;
    return {disciplines, total};
  }

  private mapToUserArray(json): User[] {
    return json.map(user => User.deserialise(user));
  }

  private deserialise(json: any): Discipline {
    json.type = UserGroupType.DISCIPLINE;
    return json as Discipline;
  }

  private deserialiseArray(json: any[]): Discipline[] {
    return json.map(j => this.deserialise(j));
  }
}
